import {
  InfiniteList,
  RecordContextProvider,
  useDefaultTitle,
  useTranslate,
  useNotify,
  useDataProvider,
  useInfiniteListController,
} from "react-admin";
import {
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Typography,
  Button,
  Box,
  CircularProgress,
} from "@mui/material";
import LaunchIcon from "@mui/icons-material/Launch";

interface MyCourse {
  id: number;
  title: string;
  imageData?: string;
}

const MyCoursesTitle = () => {
  const title = useDefaultTitle();
  const { defaultTitle } = useInfiniteListController();
  return (
    <>
      <title>{`${title} - ${defaultTitle}`}</title>
      <span>{defaultTitle}</span>
    </>
  );
};

export const MyCoursesList = () => (
  <Box sx={{ backgroundColor: (theme) => theme.palette.background.default, minHeight: "100vh", py: 4, px: { xs: 2, sm: 4, md: 8 } }}>
    {/* Rectangle Banner as the image itself with overlayed text and button */}
    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', mb: 4 }}>
      <img
        src="/assets/images/grupo_agape_banner.jpg"
        alt="Grupo Ágape Banner"
        style={{
          width: '100%',
          maxWidth: 1350,
          height: 'auto',
          borderRadius: 20,
          boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.18)',
          display: 'block',
          objectFit: 'cover',
        }}
      />
    </Box>
    <InfiniteList
      perPage={6}
      actions={false}
      title={<MyCoursesTitle />}
      component={Box}
    >
      <MyCoursesGrid />
    </InfiniteList>
  </Box>
);

const CourseCard = ({ record }: { record: MyCourse }) => {
  const notify = useNotify();
  const translate = useTranslate();
  const dataProvider = useDataProvider();

  const handleAccessCourse = async () => {
    if (!record?.id) {
      notify(translate("resources.my-courses.messages.courseNotFound"), {
        type: "error",
      });
      return;
    }

    // Open a blank window synchronously for Safari compatibility
    const win = window.open("", "_blank");
    try {
      const data = await dataProvider.httpRequest(
        `my-courses/${record.id}/access-link`,
      );
      const link = data?.data?.link;
      if (link && win) {
        win.location.href = link;
      } else if (!win) {
        notify(translate("resources.my-courses.messages.accessError"), {
          type: "error",
        });
      }
    } catch (error) {
      if (win) {
        win.close();
      }
      notify(translate("resources.my-courses.messages.accessError"), {
        type: "error",
      });
    }
  };

  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        p: 2,
        boxShadow: 6,
        borderRadius: 3,
        border: "1.5px solid",
        borderColor: "rgba(25, 118, 210, 0.12)",
        backgroundColor: (theme) => theme.palette.background.paper,
        transition: "transform 0.2s, box-shadow 0.2s, border-color 0.2s",
        maxWidth: 370,
        width: '100%',
        margin: '0 auto',
        "&:hover": {
          transform: "translateY(-6px) scale(1.03)",
          boxShadow: 12,
          borderColor: "rgba(25, 118, 210, 0.24)",
        },
      }}
    >
      <CardMedia
        component="img"
        sx={{
          height: 180,
          borderRadius: 2,
          objectFit: "fill",
          mb: 2,
          maxWidth: '100%',
        }}
        image={record?.imageData || "/assets/images/imagem_indisponivel.png"}
        alt={record.title}
        onError={(e) => {
            const target = e.target as HTMLImageElement;
            target.src = "/assets/images/imagem_indisponivel.png"
        }}
      />
      <CardContent sx={{ flexGrow: 1, p: 0 }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          {record.title}
        </Typography>
      </CardContent>
      <CardActions sx={{ mt: 2, p: 0 }}>
        <Button
          fullWidth
          variant="contained"
          color="primary"
          onClick={handleAccessCourse}
          startIcon={<LaunchIcon />}
          sx={{
            borderRadius: 2,
            height: 44,
            fontWeight: 600,
            letterSpacing: 1,
          }}
        >
          {translate("resources.my-courses.buttons.access")}
        </Button>
      </CardActions>
    </Card>
  );
};

const MyCoursesGrid = () => {
  const {
    data,
    isLoading,
    error,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteListController<MyCourse>();
  const translate = useTranslate();

  if (isLoading) {
    return (
      <Box sx={{ textAlign: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <Typography color="error">{translate("ra.page.error")}</Typography>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
        <Grid container spacing={4} sx={{ mt: 0, maxWidth: 1400, justifyContent: 'center' }} >
          {data?.map((record) => (
            <Grid key={record.id} size={{ xs: 12, sm: 6, md: 4, lg: 4 }} sx={{ display: 'flex', justifyContent: 'center' }}>
              <RecordContextProvider value={record}>
                <CourseCard record={record} />
              </RecordContextProvider>
            </Grid>
          ))}
        </Grid>
      </Box>

      {hasNextPage && (
        <Box sx={{ textAlign: "center", mt: 4 }}>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
            sx={{ px: 4, py: 1 }}
          >
            {isFetchingNextPage ? (
              <CircularProgress size={24} />
            ) : (
              translate("ra.action.load_more")
            )}
          </Button>
        </Box>
      )}
    </>
  );
};
